import { useState } from 'react';
import Avatar from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import { formatDate, formatMoney } from '@/utils/format';
import type { CampaignBrief, CampaignAttachment, AttachmentKind } from '../types';
import { contentTypeOptions } from '../types';
import type { ContractDraft } from '../types';
import type { CreatorProfile as Creator } from '@/types/portal';
import { formatFileSize, formatLabels, matchedFormats, onboardingLink, personalizeMessage } from '../helpers';
import { PLATFORM } from '@/config/platform';

interface InviteStepProps {
  brief: CampaignBrief;
  creators: Creator[];
  rates: Record<string, number>;
  customContentType: string;
  message: string;
  onMessage: (value: string) => void;
  contract: ContractDraft;
  attachments: CampaignAttachment[];
  onBack: () => void;
  onSend: () => void;
  /** True while the campaign is being saved. */
  sending?: boolean;
  /** Why saving failed, shown by the Send button. */
  error?: string;
  sent: boolean;
  /** Creators actually invited; fewer than the shortlist when some were skipped. */
  invitedCount: number;
}

const attachmentMeta: Record<AttachmentKind, { label: string; icon: string; tone: string }> = {
  document: { label: 'Document', icon: 'ri-file-text-line', tone: 'bg-secondary-100 text-secondary-700' },
  image: { label: 'Image', icon: 'ri-image-2-line', tone: 'bg-accent-100 text-accent-700' },
  video: { label: 'Video', icon: 'ri-movie-2-line', tone: 'bg-primary-100 text-primary-700' },
  audio: { label: 'Audio', icon: 'ri-mic-line', tone: 'bg-secondary-100 text-secondary-700' },
};

export default function InviteStep({
  brief,
  creators,
  rates,
  customContentType,
  message,
  onMessage,
  contract,
  attachments,
  onBack,
  onSend,
  sending = false,
  error = '',
  sent,
  invitedCount,
}: InviteStepProps) {
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const link = onboardingLink(brief.name);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-accent-200 bg-accent-50 px-6 py-16 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-500 text-background-50 text-2xl">
          <i className="ri-check-line" />
        </span>
        <h2 className="mt-4 font-heading text-lg font-semibold text-accent-900">
          Invitations sent to {invitedCount} creator{invitedCount === 1 ? '' : 's'}
        </h2>
        {invitedCount < creators.length ? (
          <p className="mt-1 max-w-md text-sm text-secondary-900">
            {creators.length - invitedCount} of the {creators.length} shortlisted weren&apos;t invited: already on
            this campaign, or no longer on {PLATFORM.name}. Add them from the campaign&apos;s Creators tab if needed.
          </p>
        ) : null}
        <p className="mt-1 max-w-md text-sm text-accent-800">
          {brief.name || 'Your campaign'} is now in <strong>Recruiting</strong>. CPaaS will track
          responses, contract signatures and KYC, and alert you when the pool is ready to activate.
        </p>
        <div className="mt-5">
          <Button icon="ri-megaphone-line" onClick={onBack}>
            Open campaign
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50 text-base">
            <i className="ri-link" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-sm font-semibold text-accent-900">
              Onboarding link
            </h2>
            <p className="text-xs text-accent-700">For creators outside {PLATFORM.name}</p>
            <p className="mt-0.5 text-xs text-accent-800">
              Copy this link and share it externally — creators register or log in through it and
              are enrolled automatically.
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
              <code className="min-w-0 flex-1 truncate rounded-md border border-accent-200 bg-background-50 px-3 py-2 text-xs font-label text-foreground-700">
                {link}
              </code>
              <Button
                variant="accent"
                size="sm"
                icon={copied ? 'ri-check-line' : 'ri-file-copy-line'}
                onClick={copyLink}
                className="shrink-0"
              >
                {copied ? 'Copied' : 'Copy link'}
              </Button>
            </div>
            <p className="mt-2 flex items-center gap-1.5 text-[11px] text-accent-700">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={brief.linkExpiresOn ? 'ri-time-line' : 'ri-infinity-line'} />
              </span>
              {brief.linkExpiresOn
                ? `Link expires on ${formatDate(brief.linkExpiresOn)}`
                : 'No expiry — this link stays active'}
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700 text-base">
            <i className="ri-file-text-line" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-sm font-semibold text-foreground-950">
              Campaign Description
            </h2>
            <p className="text-xs text-foreground-500">Shared with every creator you invite.</p>
            {brief.description.trim() ? (
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">
                {brief.description.trim()}
              </p>
            ) : (
              <p className="mt-2 text-sm italic text-foreground-400">
                No description yet — add one in the campaign brief to include it in this invite.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700 text-base">
            <i className="ri-list-check-2" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-sm font-semibold text-foreground-950">
              Deliverables
            </h2>
            <p className="text-xs text-foreground-500">
              Exactly what each creator is expected to produce.
            </p>
            {brief.deliverables.trim() ? (
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">
                {brief.deliverables.trim()}
              </p>
            ) : (
              <p className="mt-2 text-sm italic text-foreground-400">
                No deliverables yet — add them in the campaign brief so creators know what to produce.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700 text-base">
            <i className="ri-attachment-2" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-sm font-semibold text-foreground-950">
              Attached brief files
            </h2>
            <p className="text-xs text-foreground-500">
              Creators can open these before they accept or decline.
            </p>
            {attachments.length > 0 ? (
              <ul className="mt-3 space-y-2">
                {attachments.map((file) => {
                  const meta = attachmentMeta[file.kind];
                  return (
                    <li
                      key={file.id}
                      className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100 px-3 py-2.5"
                    >
                      {file.previewUrl ? (
                        <span className="h-10 w-10 shrink-0 overflow-hidden rounded-md border border-background-200">
                          <img
                            src={file.previewUrl}
                            alt={file.name}
                            className="h-full w-full object-cover"
                          />
                        </span>
                      ) : (
                        <span
                          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-lg ${meta.tone}`}
                        >
                          <i className={meta.icon} />
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground-800">
                          {file.name}
                        </p>
                        <p className="truncate text-[11px] text-foreground-500">
                          {meta.label} · {formatFileSize(file.size)}
                        </p>
                      </div>
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-background-200 bg-background-50 px-2 py-0.5 text-[11px] font-label text-foreground-500">
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-download-2-line" />
                        </span>
                        {meta.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="mt-2 text-sm italic text-foreground-400">
                No files attached to this brief — add them in the campaign brief to share them with creators.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h2 className="font-heading text-sm font-semibold text-foreground-950">
          Invitation message
        </h2>
        <p className="mt-0.5 text-xs text-foreground-500">
          CPaaS personalises this per creator and includes their brief and rate.
        </p>
        <textarea
          value={message}
          onChange={(e) => onMessage(e.target.value)}
          rows={6}
          maxLength={500}
          className="mt-3 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
        />
        <p className="mt-1 text-right text-[11px] text-foreground-400">{message.length}/500</p>
        <p className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-foreground-500">
          <i className="ri-price-tag-3-line text-foreground-400" />
          Placeholders:
          {['{{creator}}', '{{campaign}}', '{{brand}}', '{{description}}', '{{deliverables}}', '{{contentTypes}}', '{{rate}}'].map(
            (token) => (
              <span
                key={token}
                className="rounded-sm bg-background-100 px-1.5 py-0.5 font-label text-foreground-600"
              >
                {token}
              </span>
            ),
          )}
        </p>
        {contract.fileName ? (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-accent-200 bg-accent-50 p-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50 text-base">
              <i className="ri-file-paper-2-line" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-accent-900">{contract.fileName}</p>
              <p className="text-xs text-accent-700">
                {contract.fileType} · {contract.fileSize} · sent for e-signature with the invite
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-label text-accent-800">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-lock-2-line" />
              </span>
              Hire gated on signature
            </span>
          </div>
        ) : null}
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-center justify-between gap-3 border-b border-background-200 px-5 py-3">
          <h3 className="font-heading text-sm font-semibold text-foreground-950">
            Inviting {creators.length} creators
          </h3>
          <span className="text-[11px] text-foreground-500">Each preview is personalised</span>
        </div>
        <div className="divide-y divide-background-100">
          {creators.map((creator) => {
            const matched = matchedFormats(creator, brief.contentTypes);
            const custom = customContentType.trim();
            const contentLabel =
              [formatLabels(matched), custom].filter(Boolean).join(', ') || 'the agreed formats';
            const rate = rates[creator.id] ?? creator.rate ?? 0;
            const preview = personalizeMessage(message, {
              creator: creator.name,
              campaign: brief.name || 'your campaign',
              brand: brief.brandClient || 'the brand',
              description: brief.description,
              deliverables: brief.deliverables,
              contentTypes: contentLabel,
              rate: formatMoney(rate, brief.currency),
            });
            const open = previewId === creator.id;
            return (
              <div key={creator.id} className="px-5 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Avatar src={creator.avatar} alt={creator.name} size="sm" />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{creator.name}</p>
                      <p className="text-xs text-foreground-500">{creator.handle}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-1 text-[11px] font-label text-accent-700">
                      <i className="ri-mail-send-line" />
                      Email invite
                    </span>
                    <button
                      type="button"
                      onClick={() => setPreviewId(open ? null : creator.id)}
                      className="inline-flex items-center gap-1 rounded-md border border-background-300 px-2 py-1 text-[11px] font-label text-foreground-600 cursor-pointer hover:border-secondary-300 hover:text-foreground-800"
                    >
                      <i className={open ? 'ri-arrow-up-s-line' : 'ri-eye-line'} />
                      {open ? 'Hide preview' : 'Preview'}
                    </button>
                  </div>
                </div>

                <div className="mt-2 flex flex-wrap gap-1.5">
                  {matched.map((id) => {
                    const option = contentTypeOptions.find((type) => type.id === id);
                    if (!option) return null;
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 rounded-full border border-secondary-200 bg-secondary-50 px-2 py-0.5 text-[11px] text-secondary-900"
                      >
                        <span aria-hidden="true">{option.emoji}</span>
                        {option.label}
                      </span>
                    );
                  })}
                  {custom ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-accent-300 bg-accent-50 px-2 py-0.5 text-[11px] text-accent-800">
                      <span aria-hidden="true">📌</span>
                      {custom}
                    </span>
                  ) : null}
                </div>

                {open ? (
                  <div className="mt-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-xs leading-relaxed text-foreground-700">
                    <p className="mb-1 flex items-center gap-1 text-[10px] font-label uppercase tracking-wide text-foreground-400">
                      <i className="ri-mail-open-line" />
                      Personalized for {creator.name}
                    </p>
                    <p className="whitespace-pre-wrap">{preview}</p>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {error ? (
        <div role="alert" className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5" />
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="outline" icon="ri-arrow-left-line" onClick={onBack} disabled={sending}>
          Back to contract
        </Button>
        <Button icon="ri-send-plane-2-line" onClick={onSend} disabled={sending}>
          {sending ? 'Creating campaign…' : `Send ${creators.length} invitations`}
        </Button>
      </div>
    </div>
  );
}